import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackLeadConversion } from '../../lib/gtag';
import { getUtm } from '../../lib/utm';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { X, Send, CheckCircle, Package, ArrowLeft, Gift, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputDark = "h-11 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400";
const gradientBtn = "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:shadow-[0_0_30px_rgba(6,182,212,0.8)] transition-all";

const submitLead = async ({ name, phone, message, source, website = '' }) => {
  await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), message, source, website, ...getUtm() });
  trackLeadConversion();
};

const Honeypot = ({ value, onChange }) => (
  <input type="text" name="website" value={value} onChange={onChange}
    style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
    tabIndex={-1} autoComplete="off" aria-hidden="true" />
);

/* ---------- QUIZ MODAL ---------- */
const quizSteps = [
  {
    question: 'Звідки плануєте доставку?',
    options: ['🇨🇳 З Китаю', '🇺🇸 З США', '🤔 Ще не визначився'],
  },
  {
    question: 'Що плануєте везти?',
    options: ['👕 Одяг та взуття', '📱 Електроніка', '🏠 Товари для дому', '📦 Комерційна партія', '✨ Інше'],
  },
  {
    question: 'Орієнтовна вага вантажу?',
    options: ['До 10 кг', '10 – 50 кг', '50 – 200 кг', 'Понад 200 кг'],
  },
];

export const QuizModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => { setStep(0); setAnswers([]); setName(''); setPhone(''); setSent(false); };

  const pick = (option) => {
    const next = [...answers];
    next[step] = option;
    setAnswers(next);
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Введіть ім'я"); return; }
    if (!phone.trim() || phone.trim().length < 5) { toast.error('Введіть телефон'); return; }
    setSubmitting(true);
    try {
      await submitLead({
        name, phone, website,
        message: `Квіз-розрахунок: звідки — ${answers[0] || '-'}; товар — ${answers[1] || '-'}; вага — ${answers[2] || '-'}`,
        source: 'quiz',
      });
      setSent(true);
      setTimeout(() => { onClose(); reset(); }, 2800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка надсилання');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const progress = sent ? 100 : Math.round((step / (quizSteps.length + 1)) * 100);

  return (
    <div className="fixed z-[70] flex items-center justify-center p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }} data-testid="quiz-modal">
      <div className="absolute bg-black/70 backdrop-blur-sm" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-blue-500/40 bg-[#0a1224]/95 backdrop-blur-xl shadow-[0_0_60px_-10px_rgba(59,130,246,0.7)] overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:border-blue-400 transition-all flex items-center justify-center" data-testid="quiz-close">
          <X className="w-4 h-4" />
        </button>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-800">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-500 shadow-[0_0_10px_rgba(6,182,212,0.8)]" style={{ width: `${progress}%` }} />
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-8" data-testid="quiz-success">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.6)] mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-white font-bold text-xl mb-1">Дякуємо!</h4>
              <p className="text-slate-300 text-sm">Менеджер розрахує вартість і зателефонує вам найближчим часом</p>
            </div>
          ) : step < quizSteps.length ? (
            <>
              <p className="text-cyan-400 text-xs font-semibold mb-1">Крок {step + 1} з {quizSteps.length + 1}</p>
              <h3 className="text-white font-bold text-lg mb-5">{quizSteps[step].question}</h3>
              <div className="space-y-2.5">
                {quizSteps[step].options.map((opt, i) => (
                  <button key={i} onClick={() => pick(opt)}
                    className="w-full text-left px-4 py-3.5 rounded-xl bg-slate-900/60 border border-blue-500/30 text-white hover:border-cyan-400 hover:bg-slate-800/80 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all font-medium"
                    data-testid={`quiz-option-${step}-${i}`}>
                    {opt}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-4 text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="quiz-back">
                  <ArrowLeft className="w-4 h-4" /> Назад
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-cyan-400 text-xs font-semibold mb-1">Останній крок</p>
              <h3 className="text-white font-bold text-lg mb-2">Куди надіслати розрахунок?</h3>
              <p className="text-slate-400 text-sm mb-4">Менеджер підготує персональний розрахунок і зателефонує вам</p>
              <form onSubmit={handleSubmit} className="space-y-2.5">
                <Honeypot value={website} onChange={e => setWebsite(e.target.value)} />
                <Input placeholder="Ваше ім'я" value={name} onChange={e => setName(e.target.value)} className={inputDark} data-testid="quiz-name" required />
                <Input type="tel" placeholder="+380 ..." value={phone} onChange={e => setPhone(e.target.value)} className={inputDark} data-testid="quiz-phone" required />
                <Button type="submit" disabled={submitting} className={`w-full h-12 ${gradientBtn}`} data-testid="quiz-submit">
                  {submitting ? 'Відправляю...' : (<><Send className="w-4 h-4 mr-2" />Отримати розрахунок</>)}
                </Button>
              </form>
              <button onClick={() => setStep(step - 1)} className="mt-3 text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="quiz-back">
                <ArrowLeft className="w-4 h-4" /> Назад
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- STICKY MOBILE CTA ---------- */
export const StickyMobileCTA = ({ onOpen }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-[55] p-3 bg-gradient-to-t from-[#060a13] via-[#060a13]/95 to-transparent pt-6" data-testid="sticky-mobile-cta">
      <button onClick={onOpen}
        className={`w-full h-13 py-3.5 rounded-full flex items-center justify-center gap-2 text-base ${gradientBtn} animate-pulse-slow`}
        data-testid="sticky-cta-btn">
        <Calculator className="w-5 h-5" />
        Розрахувати доставку
      </button>
    </div>
  );
};

/* ---------- EXIT INTENT POPUP ---------- */
export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('cargoags_exit_done')) return;
    const lastClosed = localStorage.getItem('cargoags_exit_closed_at');
    if (lastClosed && (Date.now() - parseInt(lastClosed)) / 3600000 < 24) return;

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 10000);
    const onMouseOut = (e) => {
      if (!armed) return;
      if (e.clientY <= 10 && !e.relatedTarget) {
        setOpen(true);
        document.removeEventListener('mouseout', onMouseOut);
      }
    };
    document.addEventListener('mouseout', onMouseOut);
    return () => { clearTimeout(armTimer); document.removeEventListener('mouseout', onMouseOut); };
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    localStorage.setItem('cargoags_exit_closed_at', String(Date.now()));
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Введіть ім'я"); return; }
    if (!phone.trim() || phone.trim().length < 5) { toast.error('Введіть телефон'); return; }
    setSubmitting(true);
    try {
      await submitLead({ name, phone, message: 'Запит безкоштовного розрахунку (exit-intent)', source: 'exit-intent' });
      setSent(true);
      localStorage.setItem('cargoags_exit_done', '1');
      setTimeout(handleClose, 2800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка надсилання');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed z-[70] flex items-center justify-center p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }} data-testid="exit-popup">
      <div className="absolute bg-black/70 backdrop-blur-sm" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={handleClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-blue-500/40 bg-[#0a1224]/95 backdrop-blur-xl shadow-[0_0_60px_-10px_rgba(59,130,246,0.7)] overflow-hidden animate-fadeIn">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
        <button onClick={handleClose} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:border-blue-400 transition-all flex items-center justify-center" data-testid="exit-popup-close">
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-6">
          {sent ? (
            <div className="text-center py-6" data-testid="exit-popup-success">
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.6)] mb-3">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-bold text-lg mb-1">Заявку прийнято!</h4>
              <p className="text-slate-300 text-sm">Менеджер скоро зателефонує вам</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(249,115,22,0.5)] flex-shrink-0">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg leading-tight">Зачекайте! Не йдіть з порожніми руками</h3>
                  <p className="text-slate-400 text-sm mt-1">Залиште номер — і отримайте <span className="text-cyan-400 font-semibold">безкоштовний розрахунок доставки</span> вашого товару за 15 хвилин</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-2.5">
                <Honeypot value={website} onChange={e => setWebsite(e.target.value)} />
                <Input placeholder="Ваше ім'я" value={name} onChange={e => setName(e.target.value)} className={inputDark} data-testid="exit-popup-name" required />
                <Input type="tel" placeholder="+380 ..." value={phone} onChange={e => setPhone(e.target.value)} className={inputDark} data-testid="exit-popup-phone" required />
                <Button type="submit" disabled={submitting} className={`w-full h-12 ${gradientBtn}`} data-testid="exit-popup-submit">
                  {submitting ? 'Відправляю...' : (<><Package className="w-4 h-4 mr-2" />Отримати розрахунок безкоштовно</>)}
                </Button>
                <p className="text-[10px] text-slate-500 text-center">Натискаючи кнопку, ви погоджуєтесь на обробку даних</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- WRAPPER ---------- */
export const LeadBoosters = () => {
  const [quizOpen, setQuizOpen] = useState(false);
  return (
    <>
      <StickyMobileCTA onOpen={() => setQuizOpen(true)} />
      <QuizModal open={quizOpen} onClose={() => setQuizOpen(false)} />
      <ExitIntentPopup />
    </>
  );
};
